// Shared vocabulary for the configuration panel: what counts as a section, and
// when a section still needs the user's attention. Used by every screen that
// edits an integration's action configurations (Route flow map, destination
// page/modal, new-Route wizard) so the badges mean the same thing everywhere.

// The least an integration record has to look like to be edited here.
export type ConfigTarget = {
    id: string;
    actions: any[];            // integration.type.actions (schema + ui_schema)
    configurations: any[];     // integration.configurations
};

export const isFlowAction = (a: any) => a.type === "pull" || a.type === "push";
export const isAuth = (a: any) => a.type === "auth" || a.value === "auth";
// A flow action whose direction isn't carrying data right now: editable as an
// FYI, but never badged — nothing blocks the flow without it. Auth wins over
// type: an auth action typed `pull` sits under Credentials and is always
// required. One predicate for the nav badge, the FYI note, and the needs-setup
// count, so those surfaces cannot drift apart.
export const isDormantAction = (a: any, dormantTypes?: Set<string>) =>
    !isAuth(a) && !!dormantTypes?.has(a.type);
// A pull action's own on/off switch. `run_on_schedule` is inherited from the
// connector framework's base pull configuration, so it is the connector telling
// us "this action only runs if you ask for it" — off by default. An action that
// carries it is inert until the user switches it on, which is why its fields are
// read-only and its required fields don't block a save while it's off.
export const SCHEDULE_TOGGLE_FIELD = "run_on_schedule";

export const hasScheduleToggle = (a: any) =>
    (a?.schema?.properties ?? {})[SCHEDULE_TOGGLE_FIELD]?.type === "boolean";

// What the switch reads before the user has touched it — the connector's own
// default, which is off for every pull action we've seen but is the connector's
// call, not ours to assume.
export const scheduleToggleDefault = (a: any) =>
    (a?.schema?.properties ?? {})[SCHEDULE_TOGGLE_FIELD]?.default === true;

// Actions with no switch are always on: nothing offers to turn them off, so
// treating them as off would silently stop validating their required fields.
export const actionIsSwitchedOn = (a: any, data: any) => {
    if (!hasScheduleToggle(a)) return true;
    const value = (data ?? {})[SCHEDULE_TOGGLE_FIELD];
    return typeof value === "boolean" ? value : scheduleToggleDefault(a);
};

export const hasFields = (a: any) => a.schema?.properties && Object.keys(a.schema.properties).length > 0;
export const slugOf = (a: any) => a.value || a.id;

// Carries a button that runs it. Such an action is never "empty", whatever its
// schema holds, and is never hidden as a result: the button IS the section.
export const isExecutableAction = (a: any) => a?.schema?.is_executable === true;

// Worth rendering as a section: it has fields to fill in, or it runs. Everything
// else has nothing to put in the form, and a heading over an empty box read as a
// bug.
export const isSectionWorthy = (a: any) => hasFields(a) || isExecutableAction(a);

export const isBlank = (v: any) =>
    v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export const configFor = (target: ConfigTarget | undefined, action: any) =>
    target?.configurations?.find((c: any) => c.action?.id === action?.id);

// Which of an action's required fields have no value in the data given. Takes
// the data rather than a ConfigTarget because the creation screens keep their
// draft configurations in local state, in a different shape from a saved
// integration's — they have no record to look the values up in yet.
export const missingRequiredIn = (a: any, data: any): string[] => {
    if (!hasFields(a)) return [];
    // Switched off by its own toggle: the connector never runs it, so its
    // required fields are not outstanding work. One chokepoint for the nav
    // counts, the "!" badge, the per-field hints and the Quick Start steps, so
    // none of them can claim a disabled section owes something.
    if (!actionIsSwitchedOn(a, data)) return [];
    const required: string[] = a.schema?.required ?? [];
    if (required.length === 0) return [];
    return required.filter((f) => isBlank((data ?? {})[f]));
};

// Which of an action's required fields have no value yet. A saved configuration
// isn't proof of setup — actions are often created with defaults that leave the
// required fields empty, so look at the data itself.
export const missingRequiredFields = (target: ConfigTarget, a: any): string[] =>
    missingRequiredIn(a, configFor(target, a)?.data);

// Drives the "!" badge on nav items, nodes and arrows — keep the surfaces in sync
// by going through this one predicate.
export const actionNeedsSetup = (target: ConfigTarget, a: any) => missingRequiredFields(target, a).length > 0;

// No configuration record at all: whatever the form shows is schema defaults that
// have never been sent to Gundi.
export const actionIsUnsaved = (target: ConfigTarget, a: any) => !configFor(target, a);

// Save is worth offering even when the user hasn't edited anything: either the
// action was never saved, or what is saved is missing required fields the form is
// meanwhile showing defaults for. Committing the form as it stands fills them in —
// without this the "!" badge is unclearable, since nothing is dirty.
export const actionCanCommitForm = (target: ConfigTarget, a: any) =>
    actionIsUnsaved(target, a) || actionNeedsSetup(target, a);
